import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Menu, X, Search, PhoneCall, ChevronDown, Smartphone, Wifi, Zap, Shield, Tv, ArrowRight, Globe, Home } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage, type Language } from '../i18n/LanguageContext';
import { resolveSearchRoute } from '../utils/search';
import { trackPhoneClick, trackPartnerClick } from '../utils/analytics';

interface MegaMenuItem {
  labelKey: string;
  to: string;
  icon: React.ReactNode;
  descKey: string;
}

interface MegaMenuGroup {
  labelKey: string;
  items: MegaMenuItem[];
}

const megaMenuGroups: MegaMenuGroup[] = [
  {
    labelKey: 'nav.mobilfunk',
    items: [
      { labelKey: 'nav.mobilfunk', to: '/produkte/mobilfunk', icon: <PhoneCall className="w-4 h-4" />, descKey: 'nav.vertraegeMitOhne' },
      { labelKey: 'nav.smartphones', to: '/produkte/smartphones', icon: <Smartphone className="w-4 h-4" />, descKey: 'nav.smartphones' },
      { labelKey: 'nav.tarifrechner', to: '/angebote/tarif', icon: <Search className="w-4 h-4" />, descKey: 'nav.persoenlichenTarif' },
    ],
  },
  {
    labelKey: 'nav.internetEnergie',
    items: [
      { labelKey: 'nav.internetZuhause', to: '/internet-zuhause', icon: <Wifi className="w-4 h-4" />, descKey: 'nav.dslKabelGlasfaser' },
      { labelKey: 'nav.internet', to: '/internet-anbieter-saarland', icon: <Wifi className="w-4 h-4" />, descKey: 'nav.lokaleAnbieter' },
      { labelKey: 'nav.stromGas', to: '/strom-gas', icon: <Zap className="w-4 h-4" />, descKey: 'nav.bis400Sparen' },
      { labelKey: 'nav.lokaleStromtarife', to: '/strom-dillingen', icon: <Zap className="w-4 h-4" />, descKey: 'nav.lokaleStromtarife' },
    ],
  },
  {
    labelKey: 'nav.geraeteService',
    items: [
      { labelKey: 'nav.geraeteZubehoer', to: '/produkte/geraete', icon: <Tv className="w-4 h-4" />, descKey: 'nav.tabletsWatchesAudio' },
      { labelKey: 'nav.blogRatgeber', to: '/blog', icon: <ArrowRight className="w-4 h-4" />, descKey: 'nav.tippsVergleiche' },
      { labelKey: 'nav.ueberUns', to: '/uber-uns', icon: <ArrowRight className="w-4 h-4" />, descKey: 'nav.lokalerPartner' },
    ],
  },
];

const topBarLinks = [
  { to: '/handyvertrag-voelklingen', label: 'Völklingen' },
  { to: '/handyvertrag-saarbruecken', label: 'Saarbrücken' },
  { to: '/internet-saarlouis', label: 'Saarlouis' },
  { to: '/strom-dillingen', label: 'Dillingen' },
];

const Header: React.FC = () => {
  const { t, lang, setLang } = useLanguage();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [megaMenuOpen, setMegaMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [langOpen, setLangOpen] = useState(false);
  const navigate = useNavigate();
  const searchRef = useRef<HTMLInputElement>(null);
  const megaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (searchOpen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [searchOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (megaRef.current && !megaRef.current.contains(e.target as Node)) {
        setMegaMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(resolveSearchRoute(searchQuery));
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

  const handleLangChange = (newLang: Language) => {
    setLang(newLang);
    setLangOpen(false);
  };

  return (
    <header className={`fixed w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'}`}>
      {/* Top Bar */}
      <div className="hidden lg:block bg-primary-900 text-white text-xs">
        <div className="container-custom">
          <div className="flex items-center justify-between py-1.5">
            <div className="flex items-center gap-4">
              <span className="text-primary-200">{t('nav.lokaleBeratung')}</span>
              {topBarLinks.map((link) => (
                <Link key={link.to} to={link.to} className="text-primary-100 hover:text-white transition-colors">
                  {link.label}
                </Link>
              ))}
            </div>
            <div className="flex items-center gap-4">
              <a href="tel:+491797473491" onClick={() => trackPhoneClick('header_topbar')} className="flex items-center gap-1.5 text-primary-100 hover:text-white transition-colors">
                <PhoneCall className="w-3 h-3" /> +49 179 7473491
              </a>
              <div className="relative">
                <button
                  onClick={() => setLangOpen(!langOpen)}
                  className="flex items-center gap-1 text-primary-100 hover:text-white transition-colors"
                >
                  <Globe className="w-3 h-3" /> {lang}
                  <ChevronDown className={`w-3 h-3 transition-transform ${langOpen ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {langOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -5 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -5 }}
                      className="absolute right-0 mt-1 bg-white text-neutral-700 rounded-lg shadow-lg py-1 min-w-[100px]"
                    >
                      {(['DE', 'EN'] as const).map((l) => (
                        <button
                          key={l}
                          onClick={() => handleLangChange(l)}
                          className={`block w-full text-left px-3 py-1.5 text-xs hover:bg-neutral-50 transition-colors ${lang === l ? 'font-bold text-primary-600' : ''}`}
                        >
                          {l === 'DE' ? 'Deutsch' : 'English'}
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <div className="container-custom">
        <div className="flex justify-between items-center py-3">
          {/* Logo */}
          <Link to="/" className="text-2xl font-heading font-bold text-primary-600">
            Easy Vertrag
          </Link>

          {/* Desktop Navigation with Mega Menu */}
          <nav className="hidden lg:flex items-center gap-1">
            <NavLink to="/" className={({ isActive }) => `px-3 py-2 text-sm font-medium transition-colors ${isActive ? 'text-primary-600' : 'text-neutral-700 hover:text-primary-600'}`}>
              {t('nav.startseite')}
            </NavLink>

            <div
              ref={megaRef}
              className="relative"
              onMouseEnter={() => setMegaMenuOpen(true)}
              onMouseLeave={() => setMegaMenuOpen(false)}
            >
              <button className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-neutral-700 hover:text-primary-600 transition-colors">
                {t('nav.alleServices')}
                <ChevronDown className={`w-4 h-4 transition-transform ${megaMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              <AnimatePresence>
                {megaMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-full left-0 mt-1 w-[640px] bg-white rounded-2xl shadow-premium border border-neutral-100 p-6"
                  >
                    <div className="grid grid-cols-3 gap-6">
                      {megaMenuGroups.map((group) => (
                        <div key={group.labelKey}>
                          <h3 className="text-xs font-bold uppercase tracking-wide text-neutral-400 mb-3">{t(group.labelKey)}</h3>
                          <div className="space-y-1">
                            {group.items.map((item) => (
                              <Link
                                key={item.to}
                                to={item.to}
                                className="flex items-start gap-2.5 p-2.5 rounded-lg hover:bg-primary-50 transition-colors group"
                              >
                                <span className="text-primary-500 mt-0.5">{item.icon}</span>
                                <div>
                                  <p className="text-sm font-semibold text-neutral-800 group-hover:text-primary-600 transition-colors">{t(item.labelKey)}</p>
                                  <p className="text-xs text-neutral-400">{t(item.descKey)}</p>
                                </div>
                              </Link>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <NavLink to="/produkte/smartphones" className={({ isActive }) => `px-3 py-2 text-sm font-medium transition-colors ${isActive ? 'text-primary-600' : 'text-neutral-700 hover:text-primary-600'}`}>
              {t('nav.smartphones')}
            </NavLink>
            <NavLink to="/produkte/handytarif" className={({ isActive }) => `px-3 py-2 text-sm font-medium transition-colors ${isActive ? 'text-primary-600' : 'text-neutral-700 hover:text-primary-600'}`}>
              {t('nav.handytarife')}
            </NavLink>
            <NavLink to="/internet-zuhause" className={({ isActive }) => `px-3 py-2 text-sm font-medium transition-colors ${isActive ? 'text-primary-600' : 'text-neutral-700 hover:text-primary-600'}`}>
              {t('nav.internet')}
            </NavLink>
            <NavLink to="/strom-gas" className={({ isActive }) => `px-3 py-2 text-sm font-medium transition-colors ${isActive ? 'text-primary-600' : 'text-neutral-700 hover:text-primary-600'}`}>
              {t('nav.stromGas')}
            </NavLink>
            <NavLink to="/blog" className={({ isActive }) => `px-3 py-2 text-sm font-medium transition-colors ${isActive ? 'text-primary-600' : 'text-neutral-700 hover:text-primary-600'}`}>
              {t('nav.blog')}
            </NavLink>
            <NavLink to="/kontakt" className={({ isActive }) => `px-3 py-2 text-sm font-medium transition-colors ${isActive ? 'text-primary-600' : 'text-neutral-700 hover:text-primary-600'}`}>
              {t('nav.kontakt')}
            </NavLink>
          </nav>

          {/* Right Actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSearchOpen(!searchOpen)}
              className="p-2 text-neutral-600 hover:text-primary-600 hover:bg-neutral-100 rounded-lg transition-colors"
              aria-label={t('nav.suchen')}
            >
              <Search className="w-5 h-5" />
            </button>
            <a href="tel:+491797473491" onClick={() => trackPhoneClick('header_call_button')} className="hidden sm:flex btn-primary text-sm py-2 px-4">
              <PhoneCall className="w-4 h-4 mr-1.5" /> {t('nav.anrufen')}
            </a>
            <button
              className="lg:hidden p-2 text-neutral-600 hover:text-primary-600 rounded-lg transition-colors"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Menü"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <AnimatePresence>
        {searchOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="border-t border-neutral-100 bg-white overflow-hidden"
          >
            <div className="container-custom py-3">
              <form onSubmit={handleSearch} className="flex gap-2">
                <input
                  ref={searchRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('nav.searchPlaceholder')}
                  className="flex-1 px-4 py-2.5 rounded-xl border border-neutral-200 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none text-sm"
                />
                <button type="submit" className="btn-primary text-sm">
                  <Search className="w-4 h-4 mr-1.5" /> {t('nav.suchen')}
                </button>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-white border-t border-neutral-100 overflow-hidden"
          >
            <div className="container-custom py-4 max-h-[80vh] overflow-y-auto">
              <NavLink
                to="/"
                className={({ isActive }) =>
                  `flex items-center gap-2.5 py-2.5 px-3 rounded-lg transition-colors mb-2 ${isActive ? 'bg-primary-50 text-primary-600 font-medium' : 'text-neutral-700 hover:bg-neutral-50'}`
                }
                onClick={() => setIsMenuOpen(false)}
              >
                <Home className="w-4 h-4 text-primary-500" />
                <span className="text-sm font-medium">{t('nav.startseite')}</span>
              </NavLink>

              {megaMenuGroups.map((group) => (
                <div key={group.labelKey} className="mb-4">
                  <h3 className="text-xs font-bold uppercase tracking-wide text-neutral-400 mb-2 px-2">{t(group.labelKey)}</h3>
                  <div className="space-y-0.5">
                    {group.items.map((item) => (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        className={({ isActive }) =>
                          `flex items-center gap-2.5 py-2.5 px-3 rounded-lg transition-colors ${isActive ? 'bg-primary-50 text-primary-600 font-medium' : 'text-neutral-700 hover:bg-neutral-50'}`
                        }
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <span className="text-primary-500">{item.icon}</span>
                        <div>
                          <p className="text-sm font-medium">{t(item.labelKey)}</p>
                          <p className="text-xs text-neutral-400">{t(item.descKey)}</p>
                        </div>
                      </NavLink>
                    ))}
                  </div>
                </div>
              ))}
              <div className="pt-3 border-t border-neutral-100 space-y-1">
                <Link to="/kontakt" onClick={() => setIsMenuOpen(false)} className="btn-primary w-full">
                  <PhoneCall className="w-4 h-4 mr-1.5" /> {t('nav.kontaktAufnehmen')}
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
